import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api';

type AuthUser = {
  id: string;
  email: string;
  nome: string;
  tenantId: string;
  roles: string[];
  permissions: string[];
};

type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; user: AuthUser }
  | { status: 'anonymous' };

type AuthContextValue = {
  state: AuthState;
  login: (email: string, password: string, tenantSlug?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  async function refresh() {
    try {
      const user = await api.auth.me();
      setState({ status: 'authenticated', user });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setState({ status: 'anonymous' });
      } else {
        setState({ status: 'anonymous' });
      }
    }
  }

  async function login(email: string, password: string, tenantSlug?: string) {
    await api.auth.login({ email, password, tenantSlug });
    await refresh();
  }

  async function logout() {
    await api.auth.logout();
    setState({ status: 'anonymous' });
  }

  useEffect(() => {
    refresh();
  }, []);

  return <AuthContext.Provider value={{ state, login, logout, refresh }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
